import { useLanguage } from '../../context/LanguageContext';
import { motion } from 'framer-motion';
import { Home, Building2, Wrench, FileSearch, Check } from 'lucide-react';

export const Services = () => {
  const { t } = useLanguage();

  const services = [
    {
      icon: Home,
      key: 'residential',
      color: 'from-green-500 to-green-700',
      bgColor: 'bg-green-50',
      iconColor: 'text-green-600',
    },
    {
      icon: Building2,
      key: 'commercial',
      color: 'from-sky-500 to-sky-700',
      bgColor: 'bg-sky-50',
      iconColor: 'text-sky-600',
    },
    {
      icon: Wrench,
      key: 'maintenance',
      color: 'from-orange-500 to-orange-700',
      bgColor: 'bg-orange-50',
      iconColor: 'text-orange-600',
    },
    {
      icon: FileSearch,
      key: 'study',
      color: 'from-violet-500 to-violet-700',
      bgColor: 'bg-violet-50',
      iconColor: 'text-violet-600',
    },
  ];

  return (
    <section id="services" className="py-20 md:py-32 bg-stone-50" data-testid="services-section">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="font-outfit text-4xl md:text-5xl font-semibold tracking-tight text-stone-900 mb-4">
            {t('services.title')}
          </h2>
          <p className="text-lg text-stone-600 max-w-2xl mx-auto">
            {t('services.subtitle')}
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
          {services.map((service, index) => (
            <motion.div
              key={service.key}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="group bg-white rounded-2xl p-8 border border-stone-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              data-testid={`service-card-${service.key}`}
            >
              <div className="flex items-start gap-6">
                {/* Icon */}
                <div className={`shrink-0 w-16 h-16 ${service.bgColor} rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform`}>
                  <service.icon className={`w-8 h-8 ${service.iconColor}`} />
                </div>

                {/* Content */}
                <div className="flex-1">
                  <h3 className="font-outfit text-xl md:text-2xl font-semibold text-stone-900 mb-3">
                    {t(`services.${service.key}.title`)}
                  </h3>
                  <p className="text-stone-600 leading-relaxed mb-4">
                    {t(`services.${service.key}.description`)}
                  </p>

                  {/* Features */}
                  <div className="grid grid-cols-2 gap-2">
                    {t(`services.${service.key}.features`).map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm text-stone-600">
                        <Check className="w-4 h-4 text-green-500 shrink-0" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA Banner */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="mt-16 bg-gradient-to-r from-green-700 to-green-900 rounded-3xl p-8 md:p-12 text-center text-white relative overflow-hidden"
        >
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <svg width="100%" height="100%">
              <defs>
                <pattern id="dots" width="20" height="20" patternUnits="userSpaceOnUse">
                  <circle cx="2" cy="2" r="2" fill="white" />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#dots)" />
            </svg>
          </div>

          <div className="relative z-10">
            <h3 className="font-outfit text-2xl md:text-3xl font-semibold mb-4">
              {t('services.study.title')}
            </h3>
            <p className="text-green-100 mb-6 max-w-xl mx-auto">
              {t('services.study.description')}
            </p>
            <button
              onClick={() => document.getElementById('quote')?.scrollIntoView({ behavior: 'smooth' })}
              className="bg-white text-green-800 font-semibold px-8 py-3 rounded-xl hover:bg-green-50 transition-colors"
              data-testid="services-cta-button"
            >
              {t('nav.quote')}
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
